import { ComponentType } from 'react';
import { CreditCard, FileBadge, HeartPulse, IdCard, Laptop, type LucideIcon } from 'lucide-react';
import { SERVICE_REQUEST_TYPES, ServiceRequestType } from '@/constant/app-paths';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { BusinessCardMyApplications } from '../business-card/components/business-card-my-applications/business-card-my-applications';
import { BusinessCardRequests } from '../business-card/components/business-card-requests/business-card-requests';
import { ByodMyApplications } from '../byod/components/byod-my-applications/byod-my-applications';
import { ByodRequests } from '../byod/components/byod-requests/byod-requests';
import { IdCardMyApplications } from '../id-card/components/id-card-my-applications/id-card-my-applications';
import { IdCardRequests } from '../id-card/components/id-card-requests/id-card-requests';
import { MetlifeMyApplications } from '../insurance/components/metlife-my-applications/metlife-my-applications';
import { MetlifeRequests } from '../insurance/components/metlife-requests/metlife-requests';
import { NocMyApplications } from '../noc/components/noc-my-applications/noc-my-applications';
import { NocRequests } from '../noc/components/noc-requests/noc-requests';
import { SERVICE_DESK_URLS } from './service-desk.constants';
import { ApplicationBase } from './service-desk.types';
import { businessCardApi } from '../business-card/services/business-card.service';
import { byodApi } from '../byod/services/byod.service';
import { idCardApi } from '../id-card/services/id-card.service';
import { metlifeApi } from '../insurance/services/metlife.service';
import { nocApi } from '../noc/services/noc.service';

/** Any type's application, for the combined My Requests list. */
export type AnyApplication = ApplicationBase & Record<string, unknown>;

/** How many of one person's applications of a type My Requests reads (people have a handful). */
const OWN_LIMIT = 200;
const ownOf =
  (api: {
    list: (params: {
      pageNo: number;
      pageSize: number;
      filter: Record<string, unknown>;
    }) => Promise<{ items: unknown[] }>;
  }) =>
  async (userId: string) =>
    (await api.list({ pageNo: 1, pageSize: OWN_LIMIT, filter: { UserId: userId } }))
      .items as AnyApplication[];
const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : '');
const joinParts = (...parts: unknown[]) => parts.map(text).filter(Boolean).join(' · ');

export type RequestTypeConfig = {
  type: ServiceRequestType;
  /** Translation key of the type's name (generic). */
  label: string;
  icon: LucideIcon;
  /** May see their own applications of this type. */
  viewOwn: string;
  /** May see other employees' applications of this type (Team and All Requests). */
  viewRequests: string;
  /** May apply: opens the apply page, or the dialog for a business card (it has no page). */
  apply: string;
  applyIn: 'page' | 'dialog';
  /** "View Policy" beside the list (BYOD and insurance). */
  policyUrl?: string;
  Mine: ComponentType;
  Requests: ComponentType;
  /** The signed-in person's applications of this type, for the combined My Requests list. */
  listOwn: (userId: string) => Promise<AnyApplication[]>;
  /** One line about the application (its key details); `nocTypeName` names an NOC's type. */
  summary: (item: AnyApplication, nocTypeName: (id: unknown) => string) => string;
  /** Its reference number, when the type has one (NOC letter number, MetLife id). */
  reference?: (item: AnyApplication) => string;
};

/** The service request types, in the order of the type picker and the New Request menu. */
export const REQUEST_TYPES: readonly RequestTypeConfig[] = [
  {
    type: SERVICE_REQUEST_TYPES.NOC,
    label: 'NOC',
    icon: FileBadge,
    viewOwn: HRM_PERMISSIONS.SERVICE_REQUEST.NOC_VIEW,
    viewRequests: HRM_PERMISSIONS.SERVICE_REQUEST.NOC_VIEW_REQUESTS,
    apply: HRM_PERMISSIONS.SERVICE_REQUEST.NOC_APPLY,
    applyIn: 'page',
    Mine: NocMyApplications,
    Requests: NocRequests,
    listOwn: ownOf(nocApi),
    summary: (item, nocTypeName) => joinParts(nocTypeName(item.NocTypeId), item.NocCopyType),
    reference: (item) => text(item.DocumentNumber),
  },
  {
    type: SERVICE_REQUEST_TYPES.BUSINESS_CARD,
    label: 'BUSINESS_CARD',
    icon: CreditCard,
    viewOwn: HRM_PERMISSIONS.SERVICE_REQUEST.BUSINESS_CARD_VIEW,
    viewRequests: HRM_PERMISSIONS.SERVICE_REQUEST.BUSINESS_CARD_VIEW_REQUESTS,
    apply: HRM_PERMISSIONS.SERVICE_REQUEST.BUSINESS_CARD_APPLY,
    applyIn: 'dialog',
    Mine: BusinessCardMyApplications,
    Requests: BusinessCardRequests,
    listOwn: ownOf(businessCardApi),
    summary: (item) => joinParts(item.Name, item.Designation),
  },
  {
    type: SERVICE_REQUEST_TYPES.ID_CARD,
    label: 'ID_CARD',
    icon: IdCard,
    viewOwn: HRM_PERMISSIONS.SERVICE_REQUEST.ID_CARD_VIEW,
    viewRequests: HRM_PERMISSIONS.SERVICE_REQUEST.ID_CARD_VIEW_REQUESTS,
    apply: HRM_PERMISSIONS.SERVICE_REQUEST.ID_CARD_APPLY,
    applyIn: 'page',
    Mine: IdCardMyApplications,
    Requests: IdCardRequests,
    listOwn: ownOf(idCardApi),
    summary: (item) => joinParts(item.ApplicationType, item.BloodGroup),
  },
  {
    type: SERVICE_REQUEST_TYPES.BYOD,
    label: 'BYOD',
    icon: Laptop,
    viewOwn: HRM_PERMISSIONS.SERVICE_REQUEST.BYOD_VIEW,
    viewRequests: HRM_PERMISSIONS.SERVICE_REQUEST.BYOD_VIEW_REQUESTS,
    apply: HRM_PERMISSIONS.SERVICE_REQUEST.BYOD_APPLY,
    applyIn: 'page',
    policyUrl: SERVICE_DESK_URLS.devicePolicy,
    Mine: ByodMyApplications,
    Requests: ByodRequests,
    listOwn: ownOf(byodApi),
    summary: (item) => joinParts(item.DeviceModel, item.RequestDepartmentName),
  },
  {
    type: SERVICE_REQUEST_TYPES.INSURANCE,
    label: 'INSURANCE',
    icon: HeartPulse,
    viewOwn: HRM_PERMISSIONS.SERVICE_REQUEST.INSURANCE_VIEW,
    viewRequests: HRM_PERMISSIONS.SERVICE_REQUEST.INSURANCE_VIEW_REQUESTS,
    apply: HRM_PERMISSIONS.SERVICE_REQUEST.INSURANCE_APPLY,
    applyIn: 'page',
    policyUrl: SERVICE_DESK_URLS.metlifePolicy,
    Mine: MetlifeMyApplications,
    Requests: MetlifeRequests,
    listOwn: ownOf(metlifeApi),
    summary: (item) => joinParts(item.TypeOfApplication, item.PolicyApplicantName),
    reference: (item) => text(item.MetLifeIdNumber),
  },
];
